import { useWorkflowObservation } from "./use-workflow-observation";
import { useState, useCallback, useMemo } from "react";
import {
	TriggerResponseSchema,
	type WorkflowMessage,
	type WorkflowType,
} from "#/server/routes/workflow/types";
import { apiFetch } from "#/utils/api";

/**
 * Represents a single step within a workflow run
 */
export interface WorkflowStep {
	/** Step number (1-indexed) */
	step: number;
	/** Whether the step completed successfully */
	ok: boolean;
	/** Data returned from a successful step */
	data?: unknown;
	/** Error message from a failed step */
	error?: string;
	/** Error code/reason from a failed step */
	errorReason?: string;
	/** Whether the error is recoverable */
	recoverable?: boolean;
}

/**
 * Represents a tracked workflow run with its steps and status
 */
export interface TrackedWorkflowRun<TResult = unknown> {
	/** Unique workflow run identifier */
	id: string;
	/** Ordered list of completed steps */
	steps: WorkflowStep[];
	/** Current status of the workflow run */
	status: "running" | "completed" | "failed";
	/** Final result data if completed successfully */
	result?: TResult;
	/** Error information if the workflow failed */
	error?: { message: string; reason?: string; recoverable: boolean };
}

/**
 * Configuration for creating a workflow hook
 */
export interface WorkflowHookConfig<TPayload> {
	/** The workflow type identifier */
	type: WorkflowType;
	/** Realtime channel to subscribe to */
	channel: string;
	/** Optional payload validator */
	validatePayload?: (payload: TPayload) => boolean;
	/** Optional polling fallback */
	poll?: () => Promise<void> | void;
	/** Polling interval in milliseconds */
	pollIntervalMs?: number;
	/** Idle time before polling fallback engages */
	idleTimeoutMs?: number;
}

/**
 * Return type for workflow hooks created by the factory
 */
export interface WorkflowHookReturn<TPayload, TResult> {
	/** Trigger a new workflow run */
	trigger: (payload: TPayload) => Promise<string | null>;
	/** Whether a trigger request is currently in flight */
	isTriggering: boolean;
	/** All tracked workflow runs */
	runs: TrackedWorkflowRun<TResult>[];
	/** IDs of currently running workflows */
	activeRunIds: string[];
	/** Whether there are any active runs */
	hasActiveRuns: boolean;
	/** Get a specific run by ID */
	getRunById: (runId: string) => TrackedWorkflowRun<TResult> | undefined;
	/** Remove a specific run from tracking */
	clearRun: (runId: string) => void;
	/** Remove all finished runs from tracking */
	clearFinishedRuns: () => void;
}

/**
 * Factory function to create type-safe workflow hooks.
 *
 * This factory generates hooks for workflows that follow the standard pattern:
 * - Accept a payload to trigger the workflow
 * - Track multiple concurrent runs with their individual steps
 * - Subscribe to realtime updates for step/finish events
 *
 * @example
 * ```tsx
 * // Define the hook
 * export const useSplitFulfillmentOrders = createWorkflowHook<
 *   ShopifyFulfillmentOrder,
 *   SplitFulfillmentOrdersResult
 * >({
 *   type: "split-fulfillment-orders",
 *   channel: "workflow.split-fulfillment-orders",
 * });
 *
 * // Use the hook
 * function MyComponent() {
 *   const { trigger, runs, hasActiveRuns } = useSplitFulfillmentOrders();
 *   // ...
 * }
 * ```
 *
 * @param config - Configuration for the workflow hook
 * @returns A React hook for managing the specified workflow type
 */
export function createWorkflowHook<TPayload, TResult>(
	config: WorkflowHookConfig<TPayload>,
): () => WorkflowHookReturn<TPayload, TResult> {
	const { type, channel, validatePayload, poll, pollIntervalMs, idleTimeoutMs } = config;

	return function useWorkflow(): WorkflowHookReturn<TPayload, TResult> {
		/** Map of all tracked workflow runs keyed by run ID */
		const [runsMap, setRunsMap] = useState<Map<string, TrackedWorkflowRun<TResult>>>(new Map());

		/** Whether a trigger request is currently in flight */
		const [isTriggering, setIsTriggering] = useState(false);

		/** All tracked workflow runs as an array */
		const runs = useMemo(() => Array.from(runsMap.values()), [runsMap]);

		/** IDs of currently active (running) workflows */
		const activeRunIds = useMemo(
			() => runs.filter((run) => run.status === "running").map((run) => run.id),
			[runs],
		);

		/** Whether there are any active workflow runs */
		const hasActiveRuns = activeRunIds.length > 0;

		/**
		 * Get a specific workflow run by ID
		 */
		const getRunById = useCallback(
			(runId: string): TrackedWorkflowRun<TResult> | undefined => runsMap.get(runId),
			[runsMap],
		);

		/**
		 * Update a specific workflow run in the map
		 */
		const updateRun = useCallback(
			(
				runId: string,
				updater: (run: TrackedWorkflowRun<TResult>) => TrackedWorkflowRun<TResult>,
			) => {
				setRunsMap((prev) => {
					const existing = prev.get(runId);
					if (!existing) return prev;
					const next = new Map(prev);
					next.set(runId, updater(existing));
					return next;
				});
			},
			[],
		);

		/**
		 * Handle incoming workflow messages
		 */
		const handleWorkflowMessage = useCallback(
			(message: WorkflowMessage<TResult>) => {
				const { id: runId, currentStep, totalSteps } = message;

				// Ensure the run exists in our map
				setRunsMap((prev) => {
					if (!prev.has(runId)) {
						const next = new Map(prev);
						next.set(runId, { id: runId, steps: [], status: "running" });
						return next;
					}
					return prev;
				});

				if (message.ok) {
					// Success message
					const step: WorkflowStep = {
						step: currentStep,
						ok: true,
						data: message.data,
					};

					updateRun(runId, (run) => {
						const isComplete = currentStep >= totalSteps;
						return {
							...run,
							steps: [...run.steps.filter((s) => s.step !== currentStep), step],
							status: isComplete ? "completed" : "running",
							result: isComplete ? message.data : run.result,
						};
					});
				} else {
					// Error message
					const step: WorkflowStep = {
						step: currentStep,
						ok: false,
						error: message.error,
						errorReason: message.errorReason,
						recoverable: message.recoverable,
					};

					updateRun(runId, (run) => ({
						...run,
						steps: [...run.steps.filter((s) => s.step !== currentStep), step],
						status: "failed",
						error: {
							message: message.error,
							reason: message.errorReason,
							recoverable: message.recoverable,
						},
					}));
				}
			},
			[updateRun],
		);

		// Subscribe to workflow updates for all tracked runs
		useWorkflowObservation({
			enabled: hasActiveRuns,
			channels: [channel],
			events: ["workflow.finish", "workflow.step.finish"],
			poll,
			pollIntervalMs,
			idleTimeoutMs,
			onData({ event, data }) {
				if (event === "workflow.finish" || event === "workflow.step.finish") {
					handleWorkflowMessage(data as WorkflowMessage<TResult>);
				}
			},
		});

		/**
		 * Trigger a new workflow
		 *
		 * @param payload - The payload to send to the workflow
		 * @returns The workflow run ID if successful, null otherwise
		 */
		const trigger = useCallback(async (payload: TPayload): Promise<string | null> => {
			// Validate payload if validator provided
			if (validatePayload && !validatePayload(payload)) {
				console.error(`Invalid payload for ${type} workflow`);
				return null;
			}

			setIsTriggering(true);
			try {
				const response = await apiFetch(`/workflow/trigger/${type}`, {
					method: "POST",
					headers: { "Content-Type": "application/json" },
					body: JSON.stringify(payload),
				});

				if (!response.ok) {
					throw new Error(`Failed to trigger workflow: ${response.status} ${response.statusText}`);
				}

				const responseData = (await response.json()) as unknown;
				const parsed = TriggerResponseSchema.safeParse(responseData);

				if (!parsed.success) {
					throw new Error("Malformed trigger response");
				}

				const result = parsed.data;
				if (!result.ok) {
					throw new Error(`Workflow trigger error: ${result.error}`);
				}

				// Initialize the new run in our tracking map
				setRunsMap((prev) => {
					const next = new Map(prev);
					next.set(result.workflowRunId, {
						id: result.workflowRunId,
						steps: [],
						status: "running",
					});
					return next;
				});

				return result.workflowRunId;
			} catch (error) {
				console.error(`Error triggering ${type} workflow:`, error);
				return null;
			} finally {
				setIsTriggering(false);
			}
		}, []);

		/**
		 * Clear a completed or failed run from tracking
		 */
		const clearRun = useCallback((runId: string) => {
			setRunsMap((prev) => {
				const next = new Map(prev);
				next.delete(runId);
				return next;
			});
		}, []);

		/**
		 * Clear all completed and failed runs from tracking
		 */
		const clearFinishedRuns = useCallback(() => {
			setRunsMap((prev) => {
				const next = new Map(prev);
				for (const [id, run] of next) {
					if (run.status !== "running") {
						next.delete(id);
					}
				}
				return next;
			});
		}, []);

		return {
			trigger,
			isTriggering,
			runs,
			activeRunIds,
			hasActiveRuns,
			getRunById,
			clearRun,
			clearFinishedRuns,
		};
	};
}
